import { useEffect, useMemo, useRef, useState } from 'react'
import { getListenerCheckSnils } from '../../../api/listeners/api'
import type { ExtractionStage } from '../../../api/listeners/document-extraction'
import { Button } from '../../ui/button'
import { recognizedSnilsNumbers } from './recognized-snils'
import css from './ui.recognition.module.scss'

type Props = {
    stage: ExtractionStage | null
    listenerID: number | string
    snils: string
    onSnils: (value: string, expectedSnils: string) => boolean
}

export const RecognizedSnils = ({ stage, listenerID, snils, onSnils }: Props) => {
    const baseline = useRef(snils)
    const apply = useRef(onSnils)
    apply.current = onSnils
    const { numbers, needsReview } = useMemo(() => recognizedSnilsNumbers(stage), [stage])
    const [selected, setSelected] = useState<number | null>(null)
    const [retry, setRetry] = useState(0)
    const [state, setState] = useState<{ phase: 'idle' | 'checking' | 'applied' | 'review'; message: string; retryable?: boolean; number?: string }>({ phase: 'idle', message: '' })
    const completedRequest = useRef('')
    const index = selected ?? (numbers.length === 1 && !needsReview ? 0 : null)
    const number = index === null ? undefined : numbers[index]

    useEffect(() => {
        if (!number) return
        const requestKey = JSON.stringify([stage?.stageID, listenerID, number, retry])
        if (completedRequest.current === requestKey) return
        const controller = new AbortController()
        setState({ phase: 'checking', message: 'Проверяем СНИЛС на дубли в базе…' })
        const unavailable = () => setState({ phase: 'review', message: 'Не удалось проверить СНИЛС на дубли в базе. Номер не перенесён. Повторите проверку.', retryable: true })
        void getListenerCheckSnils(typeof listenerID === 'number' ? listenerID : 0, number, controller.signal).then(response => {
            if (controller.signal.aborted) return
            completedRequest.current = requestKey
            const data = response.data
            if (response.status !== 200 || !data || typeof data.result !== 'boolean'
                || (data.appID !== null && (!Number.isSafeInteger(data.appID) || data.appID < 0))) {
                unavailable()
                return
            }
            if (data.result) {
                const id = typeof data.appID === 'number' && data.appID > 0 ? ` Идентификатор слушателя: ${data.appID}.` : ''
                setState({ phase: 'review', message: `Слушатель с таким СНИЛС уже зарегистрирован.${id} Номер не перенесён.` })
                return
            }
            if (!apply.current(number, baseline.current)) {
                setState({ phase: 'review', message: 'СНИЛС не перенесён: анкета или её номер уже изменены. Проверьте поле в «Основных данных».' })
                return
            }
            setState({ phase: 'applied', message: 'Дублей СНИЛС не найдено. Номер перенесён в «СНИЛС слушателя» и сохранится вместе с анкетой.', number })
        }).catch(() => {
            if (!controller.signal.aborted) { completedRequest.current = requestKey; unavailable() }
        })
        return () => controller.abort()
    }, [number, stage?.stageID, listenerID, retry])

    if (!numbers.length && !needsReview) return null
    return <section className={css.group} aria-label="Перенос СНИЛС">
        <h5>СНИЛС слушателя</h5>
        {index === null ? <>
            <p className={css.warning}>{numbers.length
                ? 'В документах несколько номеров СНИЛС или есть неоднозначный результат. Выберите номер после проверки оригинала.'
                : 'Распознанный СНИЛС требует проверки: проверьте 11 цифр и контрольное число по оригиналу.'}</p>
            {numbers.map((value, itemIndex) => <p key={value}><Button onClick={() => setSelected(itemIndex)}>Перенести СНИЛС: {value}</Button></p>)}
        </> : <>
            <p role="status" aria-live="polite" className={state.phase === 'review' ? css.warning : undefined}>{state.message}</p>
            {state.number && <p>{state.number}</p>}
            {state.retryable && <Button onClick={() => setRetry(value => value + 1)}>Повторить проверку СНИЛС</Button>}
            {state.phase === 'review' && numbers.length > 1 && <Button onClick={() => {
                setSelected(null)
                setState({ phase: 'idle', message: '' })
                setRetry(value => value + 1)
            }}>Выбрать другой СНИЛС</Button>}
        </>}
    </section>
}
